import {
    type FC,
    useContext,
    useState,
    type ReactNode,
    type Dispatch,
    type SetStateAction,
} from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';
import { useRelayEnvironment } from 'react-relay';
import {
    useGetYesNoOptions,
    useGetPreferredLanguageOptions,
} from '../helpers/sellerApplicationOptions.tsx';

import { BasicSelect } from 'dibs-elements/exports/BasicSelect.tsx';
import FormButton from './sharedComponents/FormButton.tsx';
import { Input } from 'dibs-elements/exports/Input.tsx';

import SellerFormContext from './SellerFormContext.tsx';

import { updateSellerApplicationLeadMutation } from './mutations/updateSellerApplicationLead.ts';
import { sellerApplicationFormFieldsValidation } from '../helpers/sellerApplicationFormValidator.ts';
import { trackSellerApplicationEvent } from '../helpers/sellerApplicationTracking.ts';

import {
    type AdditionalDetailsKeys,
    type FormDataAdditionalDetails,
    SINGLE_LOCATION,
    MULTIPLE_LOCATIONS,
} from './SellerApplicationFormReducer.ts';

import dibsCss from 'dibs-css';

export const stepTitle = (
    <FormattedMessage
        defaultMessage="Additional Details"
        id="SellerApplicationForm.additionalDetails.stepTitle"
    />
);

export const headerTitle = (
    <FormattedMessage
        defaultMessage="Additional Details (optional)"
        id="SellerApplicationForm.additionalDetails.headerTitle"
    />
);

const messages = defineMessages({
    preferredLanguage: {
        id: 'SellerApplicationForm.additionalDetails.preferredLanguage',
        defaultMessage: 'What language do you want to do business in? (emails, display)',
    },
    associations: {
        id: 'SellerApplicationForm.additionalDetails.associations',
        defaultMessage: 'List any associations you are a member of',
    },
    fairs: {
        id: 'SellerApplicationForm.additionalDetails.fairs',
        defaultMessage: 'List any fairs that you have participated in',
    },
    inventoryLocation: {
        id: 'SellerApplicationForm.additionalDetails.inventoryLocation',
        defaultMessage:
            'Is your inventory located in a single location or across multiple locations?',
    },
    singleLocation: {
        id: 'SellerApplicationForm.additionalDetails.singleLocation',
        defaultMessage: 'Single location',
    },
    multipleLocations: {
        id: 'SellerApplicationForm.additionalDetails.multipleLocations',
        defaultMessage: 'Multiple locations',
    },
    locationsWithinSelectedCountry: {
        id: 'SellerApplicationForm.additionalDetails.locationsWithinSelectedCountry',
        defaultMessage: 'Are all locations within {country}?',
    },
    ownInventory: {
        id: 'SellerApplicationForm.additionalDetails.ownInventory',
        defaultMessage: 'Do you own your inventory?',
    },
    consignmentBasis: {
        id: 'SellerApplicationForm.additionalDetails.consignmentBasis',
        defaultMessage: 'Do you operate on a consignment basis with our sellers?',
    },
});

type Props = { setFormError: Dispatch<SetStateAction<boolean>> };

const SellerApplicationFormAdditionalDetails: FC<Props> = ({ setFormError }) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const { state, dispatch } = useContext(SellerFormContext);
    const {
        preferredLanguage,
        associations,
        fairs,
        locations,
        locationsWithinSelectedCountry,
        ownInventory,
        consignmentBasis,
    } = state.formDataAdditionalDetails;
    const { country } = state.formDataContactDetails;

    const [errorMessages, setErrorMessages] = useState<{
        [fieldName in AdditionalDetailsKeys]?: ReactNode;
    }>({});
    const [isLoading, setIsLoading] = useState(false);
    const yesNoOptions = useGetYesNoOptions();

    const validateField = (name: AdditionalDetailsKeys, value?: string | boolean): void => {
        const { errors } = sellerApplicationFormFieldsValidation({ [name]: value });
        const error = errors?.[name];

        setErrorMessages({
            ...errorMessages,
            [name]: error,
        });
    };

    async function submit(): Promise<void> {
        const { isValid, errors } = sellerApplicationFormFieldsValidation({
            ...(preferredLanguage && { preferredLanguage }),
            ...(associations && { associations }),
            ...(fairs && { fairs }),
            ...(locations && { locations }),
            ...(locationsWithinSelectedCountry && { locationsWithinSelectedCountry }),
            ...(ownInventory && { ownInventory }),
            ...(consignmentBasis && { consignmentBasis }),
        });
        if (isValid) {
            setErrorMessages({});
            try {
                setIsLoading(true);
                await updateSellerApplicationLeadMutation(environment, {
                    recordId: state.leadId,
                    step: 'additionalDetails',
                    preferredLanguage,
                    associations,
                    fairs,
                    locations,
                    locationsWithinSelectedCountry,
                    ownInventory,
                    consignmentBasis,
                });
                trackSellerApplicationEvent({
                    type: 'professional',
                    action: 'step 4 final submit',
                    interactionName: 'application_complete',
                    stepInteractionType: 'step 4 complete',
                });
                dispatch({ type: 'SET_STEP', step: 'BUSINESS_CONFIRMATION' });
            } catch (err) {
                setIsLoading(false);
                setFormError(true);
            }
        } else {
            setIsLoading(false);
            setErrorMessages(errors);
        }
    }

    const setField = (name: AdditionalDetailsKeys, value?: string | boolean): void => {
        dispatch({
            type: 'SET_FORM_ADDITIONAL_DETAILS',
            formDataAdditionalDetails: { [name]: value },
        });
        validateField(name, value);
    };

    return (
        <div className={dibsCss.mbLarge} data-tn="application-form-business-details">
            <div className={dibsCss.wFull}>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="preferred-language"
                        name="preferred-language"
                        placeholder={intl.formatMessage(messages.preferredLanguage)}
                        hasAnimatedPlaceholder
                        options={useGetPreferredLanguageOptions()}
                        onChange={e => {
                            const value = e.target.value;
                            setField('preferredLanguage', value);
                        }}
                        errorMessage={errorMessages?.preferredLanguage}
                        hasError={!!errorMessages?.preferredLanguage}
                        disabled={isLoading}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <Input
                        placeholder={intl.formatMessage(messages.associations)}
                        hasAnimatedPlaceholder
                        dataTn="associations"
                        name="associations"
                        onChange={e => {
                            const value = e.target.value;
                            setField('associations', value);
                        }}
                        errorMessage={errorMessages?.associations}
                        hasError={!!errorMessages?.associations}
                        disabled={isLoading}
                        maxLength={1000}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <Input
                        placeholder={intl.formatMessage(messages.fairs)}
                        hasAnimatedPlaceholder
                        dataTn="fairs"
                        name="fairs"
                        onChange={e => {
                            const value = e.target.value;
                            setField('fairs', value);
                        }}
                        errorMessage={errorMessages?.fairs}
                        hasError={!!errorMessages?.fairs}
                        disabled={isLoading}
                        maxLength={1000}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="inventory-location"
                        name="inventory-location"
                        placeholder={intl.formatMessage(messages.inventoryLocation)}
                        hasAnimatedPlaceholder
                        options={[
                            {
                                label: intl.formatMessage(messages.singleLocation),
                                value: SINGLE_LOCATION,
                            },
                            {
                                label: intl.formatMessage(messages.multipleLocations),
                                value: MULTIPLE_LOCATIONS,
                            },
                        ]}
                        onChange={e => {
                            const value = e.target.value as FormDataAdditionalDetails['locations'];
                            setField('locations', value);
                        }}
                        errorMessage={errorMessages?.locations}
                        hasError={!!errorMessages?.locations}
                        disabled={isLoading}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="locations-in-selected-country"
                        name="locations-in-selected-country"
                        placeholder={intl.formatMessage(messages.locationsWithinSelectedCountry, {
                            country,
                        })}
                        hasAnimatedPlaceholder
                        options={yesNoOptions}
                        onChange={e =>
                            setField('locationsWithinSelectedCountry', e.target.value === 'true')
                        }
                        errorMessage={errorMessages?.locationsWithinSelectedCountry}
                        hasError={!!errorMessages?.locationsWithinSelectedCountry}
                        disabled={isLoading}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="self-own-inventory"
                        name="self-own-inventory"
                        placeholder={intl.formatMessage(messages.ownInventory)}
                        hasAnimatedPlaceholder
                        options={yesNoOptions}
                        onChange={e => setField('ownInventory', e.target.value === 'true')}
                        errorMessage={errorMessages?.ownInventory}
                        hasError={!!errorMessages?.ownInventory}
                        disabled={isLoading}
                    />
                </div>
                <div className={dibsCss.mbSmall}>
                    <BasicSelect
                        dataTn="consignment-basis"
                        name="consignment-basis"
                        placeholder={intl.formatMessage(messages.consignmentBasis)}
                        hasAnimatedPlaceholder
                        options={yesNoOptions}
                        onChange={e => setField('consignmentBasis', e.target.value === 'true')}
                        errorMessage={errorMessages?.consignmentBasis}
                        hasError={!!errorMessages?.consignmentBasis}
                        disabled={isLoading}
                    />
                </div>
                <FormButton
                    isLoading={isLoading}
                    onSubmit={submit}
                    submitCopy={
                        <FormattedMessage
                            defaultMessage="Submit Application"
                            id="SellerApplicationForm.additionalDetails.submitApplication"
                        />
                    }
                />
            </div>
        </div>
    );
};

export default SellerApplicationFormAdditionalDetails;
